/**
 * ONE reusable auth-return mechanism.
 * Remembers the exact internal page (path + query + hash) a visitor was on when
 * they clicked Sign In / Join, and returns them there after auth completes.
 * Only same-origin internal routes are accepted (no open redirects).
 */
const KEY = "ss_auth_return_to";
const AUTH_ROUTES = ["/login", "/register", "/auth/callback"];

export function sanitizeReturnTo(raw) {
  if (!raw || typeof raw !== "string") return null;
  let v = raw.trim();
  try { v = decodeURIComponent(v); } catch { /* keep raw */ }
  if (!v.startsWith("/") || v.startsWith("//") || v.startsWith("/\\")) return null;
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(v)) return null;
  if (AUTH_ROUTES.some((r) => v === r || v.startsWith(r + "?") || v.startsWith(r + "/"))) return null;
  return v;
}

export function currentHref() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export function rememberReturnTo(to) {
  const safe = sanitizeReturnTo(to);
  if (safe) sessionStorage.setItem(KEY, safe);
  return safe;
}

export function peekReturnTo() {
  return sanitizeReturnTo(sessionStorage.getItem(KEY));
}

export function consumeReturnTo(fallback = "/app/dashboard") {
  const v = peekReturnTo();
  sessionStorage.removeItem(KEY);
  return v || fallback;
}

/** Resolve the destination for an auth page from ?next, router state, or storage. */
export function resolveReturnTo(location, fallback = "/app/dashboard") {
  const params = new URLSearchParams(location?.search || "");
  const fromState = location?.state?.from;
  const stateHref = fromState ? `${fromState.pathname || ""}${fromState.search || ""}${fromState.hash || ""}` : null;
  return sanitizeReturnTo(params.get("next")) || sanitizeReturnTo(stateHref) || peekReturnTo() || fallback;
}

export function loginHref(from) {
  const to = sanitizeReturnTo(from || currentHref());
  return to ? `/login?next=${encodeURIComponent(to)}` : "/login";
}

export function registerHref(from) {
  const to = sanitizeReturnTo(from || currentHref());
  return to ? `/register?next=${encodeURIComponent(to)}` : "/register";
}
